import { cvLanguageSkillsAPI } from 'api/CvAPI'
import { IItemDraftHandle } from 'types/hocTypes'
import { LanguagesData } from 'api/UtilsAPI'
import { useLocalStorage } from 'hooks/data'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ICv, ILanguageSkill } from 'types/cvsInterfaces'
import { getLanguageInitialData } from 'Components/reusable/DataContext/InitialData'
import { languageSchema } from 'Components/reusable/DataContext/ValidationSchema'
import withProfileCard from 'Components/reusable/HOC/withProfileCard'
import EditButton from 'Components/reusable/IconButtons/EditButton'
import CustomTable from 'Components/reusable/Tables/CustomTable'
import LanguageModal from './LanguageModal'
import SkillLevelChip from 'Components/reusable/CaleoCustomComponents/SkillLevelChip'
import { ColumnDef, PaginationState, VisibilityState } from '@tanstack/react-table'

/** @notExported  */
interface ILanguageCardProps {
  /** Is card editable. */
  editable: boolean
  /** CV item. */
  cv: ICv
  /** Function for setting updated date. */
  setUpdatedAt: () => void
  /** Function for replacing or adding item. */
  replaceOrAddItem: (newItem: ILanguageSkill | { deleted: number }) => ILanguageSkill[]
  /** Function for replacing or adding items. */
  replaceOrAddItems: (incomingItems: ILanguageSkill[]) => ILanguageSkill[]
  /** Function for opening item. */
  openItem: (item: ILanguageSkill) => void
  /** Edited item. */
  editedItem: ILanguageSkill | null
  /** Function for closing item. */
  closeItem: () => void
  /** Items. */
  items: ILanguageSkill[]
  /** Header text. */
  header: string
  /** Languages. */
  languages: LanguagesData
  /** Function for updating overview data. */
  updateOverviewData: (type: string, data: unknown | unknown[]) => void
  /** Function for updating progress. */
  updateProgress: () => void
  /** Dialog drafts from the CV wizard (see `withProfileCard`'s `itemDraft`). */
  itemDraft?: IItemDraftHandle
  /** Function to update language skills */
  setLanguageSkills: (items: ILanguageSkill[]) => void
}

/**
 * Language skill card.
 *
 * @returns Language skill card component.
 * @notExported
 */
const LanguageCard: React.FC<ILanguageCardProps> = ({
  cv,
  editable,
  setUpdatedAt,
  closeItem,
  replaceOrAddItem,
  replaceOrAddItems,
  openItem,
  editedItem,
  items,
  header,
  languages,
  updateOverviewData,
  updateProgress,
  itemDraft,
  setLanguageSkills,
}) => {
  const { t } = useTranslation()
  const [languageCount, setLanguageCount] = useLocalStorage('languageCount', 10)
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({})

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: languageCount ? languageCount : 25,
  })

  const columns = React.useMemo<ColumnDef<ILanguageSkill>[]>(
    () => [
      {
        id: 'name',
        header: t('language.name'),
        accessorFn: row => languages?.[row.languageCode] ?? '',
      },
      {
        id: 'level',
        header: t('language.level'),
        accessorFn: row => `${row.level}`,
        cell: ({ row }) => <SkillLevelChip level={row.original.level ?? 0} type="language" />,
      },
      {
        id: 'controls',
        header: t('admin.skills.actions'),
        enableHiding: false,
        cell: ({ row }) => {
          if (editable) {
            return (
              <EditButton
                testId={`button-language-card-edit-${row.original.id}`}
                clickAction={() => openItem(row.original)}
              />
            )
          } else {
            return ''
          }
        },
      },
    ],
    [t, languages, editable, openItem]
  )

  return (
    <>
      <CustomTable
        columns={columns}
        data={items}
        rowCount={languageCount}
        setRowCount={setLanguageCount}
        initialPageSize={10}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        setPagination={setPagination}
        pageIndex={pageIndex}
        pageSize={pageSize}
        elevation={0}
        newStyle
        id="profile-item-language-card"
      />
      {editedItem !== undefined && (
        <LanguageModal
          item={editedItem}
          draft={itemDraft?.forItem(editedItem ?? null)}
          cvId={cv.id}
          onClose={(value?: Record<string, unknown> | { id?: number; name?: string } | { newItem?: unknown }) => {
            const newItem = (value as { newItem?: unknown } | undefined)?.newItem as
              | ILanguageSkill
              | ILanguageSkill[]
              | { deleted: number }
              | undefined
            closeItem()
            if (newItem) {
              setUpdatedAt()
              if (Array.isArray(newItem)) {
                const items = replaceOrAddItems(newItem)
                setLanguageSkills(items)
                updateOverviewData('languages', items)
              } else {
                const items = replaceOrAddItem(newItem)
                setLanguageSkills(items)
                updateOverviewData('languages', items)
              }
              updateProgress()
            }
          }}
          languages={languages}
          items={items}
          schema={languageSchema()}
          initialData={getLanguageInitialData(cv.id)}
          localeBase="language"
          maxWidth="md"
          api={cvLanguageSkillsAPI}
          header={header}
          hideLanguageHelperText={true}
          addSelected
          checkFields={['languageCode', 'level']}
          noEscClose
        />
      )}
    </>
  )
}

export default withProfileCard(LanguageCard as unknown as Parameters<typeof withProfileCard>[0])
